'use client';

import React from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { Shield, ChevronRight } from 'lucide-react';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    content: `Silvergate Royals ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform at theprivateworld.app and use our services.

Please read this policy carefully. If you disagree with its terms, please discontinue use of our platform.`,
  },
  {
    id: 'information-collected',
    title: '2. Information We Collect',
    content: `We collect information you provide directly to us, including:

• **Account Information**: Name, email address, password, and profile details when you register.
• **Subscription & Billing**: Payment method details processed securely through our payment provider. We do not store full card numbers.
• **Usage Data**: Articles read, collections saved, categories browsed, and features used.
• **Communications**: Messages you send us via support or feedback channels.
• **Device & Technical Data**: IP address, browser type, operating system, referring URLs, and device identifiers collected automatically.`,
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    content: `We use the information we collect to:

• Provide, operate, and maintain our platform and editorial services
• Process your membership subscription and billing
• Personalise your discovery feed and editorial recommendations
• Send transactional communications (receipts, account alerts, password resets)
• Send editorial newsletters and curated editions (you may opt out at any time)
• Analyse usage patterns to improve our content and platform experience
• Detect, prevent, and address technical issues or fraudulent activity
• Comply with legal obligations`,
  },
  {
    id: 'sharing',
    title: '4. Sharing of Information',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

• **Service Providers**: Trusted third-party vendors who assist in operating our platform (payment processors, email delivery, analytics, hosting). These parties are contractually obligated to keep your information confidential.
• **Legal Requirements**: When required by law, court order, or governmental authority.
• **Business Transfers**: In connection with a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
• **With Your Consent**: For any other purpose with your explicit consent.`,
  },
  {
    id: 'cookies',
    title: '5. Cookies & Tracking Technologies',
    content: `We use cookies and similar tracking technologies to enhance your experience. These include:

• **Essential Cookies**: Required for the platform to function (authentication sessions, preferences).
• **Analytics Cookies**: Help us understand how visitors interact with our platform.
• **Preference Cookies**: Remember your settings and personalisation choices.

You can control cookie preferences through your browser settings. Disabling certain cookies may affect platform functionality. For full details, see our Cookie Policy.`,
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: `We retain your personal information for as long as your account is active or as needed to provide services. If you close your account, we will delete or anonymise your personal data within 90 days, except where retention is required by law or legitimate business purposes (such as resolving disputes or enforcing agreements).`,
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    content: `Depending on your location, you may have the following rights regarding your personal data:

• **Access**: Request a copy of the personal data we hold about you.
• **Correction**: Request correction of inaccurate or incomplete data.
• **Deletion**: Request deletion of your personal data ("right to be forgotten").
• **Portability**: Request transfer of your data in a machine-readable format.
• **Objection**: Object to processing of your data for certain purposes.
• **Withdrawal of Consent**: Withdraw consent at any time where processing is based on consent.

To exercise any of these rights, contact us at privacy@theprivateworld.app.`,
  },
  {
    id: 'security',
    title: '8. Data Security',
    content: `We implement industry-standard security measures to protect your personal information, including SSL/TLS encryption for data in transit, encrypted storage, and access controls. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but are committed to protecting your data with appropriate safeguards.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `Our platform is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately and we will take steps to delete it.`,
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page with an updated effective date, and where appropriate, by email notification. Your continued use of the platform after changes constitutes acceptance of the updated policy.`,
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**Silvergate Royals**
Registered Address: New York, United States
Email: privacy@theprivateworld.app
Website: theprivateworld.app`,
  },
];

export default function PrivacyPolicyPage() {
  return (
    <AppLayout currentPath="/privacy-policy">
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b border-[var(--border)] bg-[var(--secondary)]">
          <div className="max-w-3xl mx-auto px-6 py-10">
            <div className="flex items-center gap-2 text-xs text-[var(--slate-muted)] mb-4">
              <Link href="/" className="hover:text-[var(--gold)] transition-colors">Home</Link>
              <ChevronRight size={12} />
              <span>Privacy Policy</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center">
                <Shield size={16} className="text-[var(--gold)]" />
              </div>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--gold)]">Legal</span>
            </div>
            <h1 className="font-editorial text-3xl font-semibold text-[var(--foreground)] mb-2">Privacy Policy</h1>
            <p className="text-sm text-[var(--slate-muted)]">Effective date: 1 September 2025 &nbsp;·&nbsp; Last updated: 1 September 2025</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Quick Nav */}
          <div className="mb-10 p-5 bg-[var(--secondary)] border border-[var(--border)] rounded">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--slate-muted)] mb-3">Contents</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {sections?.map((s) => (
                <a
                  key={s?.id}
                  href={`#${s?.id}`}
                  className="text-xs text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors py-0.5"
                >
                  {s?.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section) => (
              <section key={section?.id} id={section?.id} className="scroll-mt-6">
                <h2 className="font-editorial text-lg font-semibold text-[var(--foreground)] mb-3 pb-2 border-b border-[var(--border)]">
                  {section?.title}
                </h2>
                <div className="text-sm text-[var(--slate-muted)] leading-relaxed whitespace-pre-line">
                  {section?.content?.split('\n')?.map((line, i) => {
                    if (line?.startsWith('• **')) {
                      const match = line?.match(/^• \*\*(.+?)\*\*: (.+)$/);
                      if (match) {
                        return (
                          <p key={i} className="mb-1.5 pl-3">
                            <span className="font-semibold text-[var(--foreground)]">{match?.[1]}:</span>{' '}
                            {match?.[2]}
                          </p>
                        );
                      }
                    }
                    if (line?.startsWith('• ')) {
                      return <p key={i} className="mb-1.5 pl-3">{line}</p>;
                    }
                    if (line?.startsWith('**') && line?.endsWith('**')) {
                      return <p key={i} className="font-semibold text-[var(--foreground)] mt-3 mb-1">{line?.replace(/\*\*/g, '')}</p>;
                    }
                    if (line === '') return <div key={i} className="h-2" />;
                    return <p key={i} className="mb-2">{line}</p>;
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-14 pt-6 border-t border-[var(--border)] flex flex-wrap gap-4 text-xs text-[var(--slate-muted)]">
            <Link href="/terms-of-service" className="hover:text-[var(--gold)] transition-colors">Terms of Service</Link>
            <Link href="/cookie-policy" className="hover:text-[var(--gold)] transition-colors">Cookie Policy</Link>
            <Link href="/" className="hover:text-[var(--gold)] transition-colors">Back to Home</Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
